import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

import type { EditorContext } from "../context";
import { nodes } from "../schema";
import type { UploadResult } from "../types";

/** The only field we put on an upload decoration. */
interface DecorationSpec {
  id?: unknown;
}

interface PlaceholderAction {
  add?: { id: string; pos: number; name: string };
  remove?: string;
}

export const uploadKey = new PluginKey<DecorationSet>("wikiUpload");

/**
 * §9 — the upload placeholder is a **decoration, not a node**: it never appears
 * in `data`, so an interrupted upload cannot leave one in the saved document.
 */
export function uploadPlaceholderPlugin(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: uploadKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, value) {
        let set = value.map(tr.mapping, tr.doc);
        const action = tr.getMeta(uploadKey) as PlaceholderAction | undefined;
        if (action?.add) {
          const element = document.createElement("span");
          element.className = "wiki-upload-placeholder";
          element.setAttribute("data-upload-id", action.add.id);
          element.textContent = `Uploading ${action.add.name}…`;
          set = set.add(tr.doc, [
            Decoration.widget(action.add.pos, element, { id: action.add.id }),
          ]);
        }
        if (action?.remove !== undefined) {
          const id = action.remove;
          set = set.remove(
            set.find(undefined, undefined, (spec: DecorationSpec) => spec.id === id),
          );
        }
        return set;
      },
    },
    props: {
      decorations: (state) => uploadKey.getState(state) ?? DecorationSet.empty,
    },
  });
}

function placeholderPos(view: EditorView, id: string): number | null {
  const set = uploadKey.getState(view.state);
  const found = set?.find(undefined, undefined, (spec: DecorationSpec) => spec.id === id);
  return found && found.length > 0 && found[0] ? found[0].from : null;
}

export type UploadKind = "image" | "video" | "attachment";

export function kindForFile(file: File): UploadKind {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "attachment";
}

function nodeForUpload(kind: UploadKind, file: File, result: UploadResult) {
  if (kind === "image") {
    return nodes.image.create({
      src: result.url,
      alt: file.name.replace(/\.[^.]+$/, ""),
      ...(result.width === undefined ? {} : { width: result.width }),
      ...(result.height === undefined ? {} : { height: result.height }),
    });
  }
  if (kind === "video") {
    return nodes.video.create({
      src: result.url,
      title: file.name,
      width: result.width ?? null,
      height: result.height ?? null,
    });
  }
  return nodes.attachment.create({
    href: result.url,
    title: file.name,
    size: String(result.size ?? file.size),
    contentType: file.type === "" ? null : file.type,
    preview: file.type === "application/pdf",
  });
}

/**
 * Upload files one at a time, in order, each behind its own placeholder
 * (§9). A failure removes that placeholder and surfaces a notice; the
 * remaining files still go.
 */
export async function uploadFiles(
  view: EditorView,
  files: readonly File[],
  at: number,
  context: EditorContext,
  kindOverride?: UploadKind,
): Promise<void> {
  const upload = context.upload;
  if (!upload) {
    context.onNotice("Uploads are not available here.");
    return;
  }
  let position = at;
  for (const file of files) {
    const id = crypto.randomUUID();
    view.dispatch(
      view.state.tr.setMeta(uploadKey, { add: { id, pos: position, name: file.name } }),
    );
    try {
      const result = await upload(file);
      const pos = placeholderPos(view, id) ?? position;
      const node = nodeForUpload(kindOverride ?? kindForFile(file), file, result);
      const tr = view.state.tr.setMeta(uploadKey, { remove: id });
      tr.replaceWith(pos, pos, node);
      view.dispatch(tr);
      position = pos + node.nodeSize;
    } catch {
      view.dispatch(view.state.tr.setMeta(uploadKey, { remove: id }));
      context.onNotice(`${file.name} could not be uploaded.`);
    }
  }
}

/** Open the browser's file picker; resolves empty when the user cancels. */
export function pickFiles(accept: string, multiple = false): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.multiple = multiple;
    input.style.display = "none";
    input.addEventListener("change", () => {
      resolve(input.files ? Array.from(input.files) : []);
      input.remove();
    });
    input.addEventListener("cancel", () => {
      resolve([]);
      input.remove();
    });
    document.body.append(input);
    input.click();
  });
}
